import {
  CellKeyDownEvent,
  ColDef,
  GridOptions,
  IRowNode,
  ProcessCellForExportParams,
} from 'ag-grid-community'
import { useMemo } from 'react'
import { RoleRow } from '../model/RoleRow'
import { useColumnDefs } from './columnDefs'
import {
  columnTypes,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { PermissionPolicyOption } from '../../../../domain/value-object/PermissionPolicy'
import { permissionPolicyService } from '../../../../domain/entity/PermissionPolicyEntity'
import { DEFAULT_COLUMN_DEF } from '../../../page-properties/bulksheet-properties'

export const useRoleGridOptions = (
  attahablePermissionPolicyOptions: PermissionPolicyOption[],
  allPermissionPolicyOptions: PermissionPolicyOption[]
): GridOptions<RoleRow> => {
  const columnDefs = useColumnDefs({
    permissionPolicyOptions: attahablePermissionPolicyOptions,
    allPermissionPolicyOptions,
  })

  const cellForClipboard = useMemo(
    () => processCellForClipboard(attahablePermissionPolicyOptions),
    [attahablePermissionPolicyOptions]
  )

  const cellFromClipboard = useMemo(
    () => processCellFromClipboard(attahablePermissionPolicyOptions),
    [attahablePermissionPolicyOptions]
  )

  return useMemo(
    () => ({
      groupHeaderHeight: 25,
      headerHeight: 45,
      floatingFiltersHeight: 25,
      rowHeight: 32,
      treeData: false,
      context: {},
      suppressLastEmptyLineOnPaste: true,

      // Row
      rowDragManaged: true,
      rowDragMultiRow: true,
      suppressMoveWhenRowDragging: true,
      enterMovesDownAfterEdit: true,
      onCellKeyDown: (params: CellKeyDownEvent) => {
        // @ts-ignore
        if (!['Delete', 'Backspace'].includes(params.event.key)) return

        const cellRanges = params.api.getCellRanges() || []
        const rowNodesChangedNameCell: IRowNode[] = []
        cellRanges.forEach(range => {
          const start = Math.min(
            range.startRow!.rowIndex,
            range.endRow!.rowIndex
          )
          const end = Math.max(range.startRow!.rowIndex, range.endRow!.rowIndex)
          range.columns.forEach(column => {
            const colDef = column.getColDef()
            if (
              typeof colDef.editable === 'function'
                ? colDef.editable(params)
                : colDef.editable
            ) {
              for (let i = start; i <= end; i++) {
                const rowNode = params.api.getDisplayedRowAtIndex(i)
                if (!rowNode) continue
                rowNode.setDataValue(column, undefined)
              }
            }
          })
        })
        params.api.refreshCells({
          rowNodes: rowNodesChangedNameCell,
          columns: ['ag-Grid-AutoColumn'],
        })
      },

      columnTypes: columnTypes(),
      components: frameworkComponents,
      defaultColDef: DEFAULT_COLUMN_DEF,
      columnDefs,

      // Copy and Paste
      processCellForClipboard: cellForClipboard,
      processCellFromClipboard: cellFromClipboard,
    }),
    [columnDefs, cellForClipboard, cellFromClipboard]
  )
}

export const processCellForClipboard =
  (attahablePermissionPolicyOptions: PermissionPolicyOption[]) =>
  ({ column, value }: ProcessCellForExportParams) => {
    const colDef = column.getColDef()
    const { field } = colDef
    if (!value) return value
    if (field === 'permissionPolicyUuids') {
      const permissionPolicyUuids: string[] = value
      if (!Array.isArray(permissionPolicyUuids)) return ''
      return attahablePermissionPolicyOptions
        .filter(o =>
          value.some(
            v => permissionPolicyService.isAttachable(o) && o.uuid === v
          )
        )
        .map(o => o.name)
        .join(',')
    }
    return value
  }

const processCellFromClipboard =
  (attahablePermissionPolicyOptions: PermissionPolicyOption[]) =>
  ({ column, value, node }: ProcessCellForExportParams<RoleRow>) => {
    const colDef: ColDef = column.getColDef()
    const { field } = colDef
    if (field === 'permissionPolicyUuids') {
      const names = splitOptionString(value)
      return names
        .map(
          name =>
            attahablePermissionPolicyOptions.find(
              o => permissionPolicyService.isAttachable(o) && o.name === name
            )?.uuid
        )
        .filter(o => o)
    }
    return value
  }

const splitOptionString = (value: string) =>
  value?.split(',').filter((v: string) => !!v && 0 < v.trim().length)
